import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '.';
import { TaskKind, TrainingCard, TrainingCardData, TrainingType, CardOption } from '../types/TrainingTypes';
import { trainingActionCreators } from './Training';

interface ChoiceState {
    task: string,
    taskAux: string | undefined,
    taskForms: string | undefined,

    solution: string,
    solutionAux: string,
    solutionForms: string,

    example: string | undefined,
    isExampleShown: boolean,

    optionCards: TrainingCard[],
    options: CardOption[],
    chosenOption: CardOption | undefined,
    wrongOption: CardOption | undefined,
    backgrounds: string[],
    shownTime: string | undefined,
    isCardDirect: boolean
}

const initialState: ChoiceState = {
    task: ' ',
    taskAux: '',
    taskForms: '',

    solution: '',
    solutionAux: '',
    solutionForms: '',

    example: '',
    isExampleShown: false,

    optionCards: [],
    options: [],
    chosenOption: undefined,
    wrongOption: undefined,
    backgrounds: ['', '', '', ''],
    shownTime: undefined,
    isCardDirect: false
}

const slice = createSlice({
    name: 'choice',
    initialState,
    reducers: {
        setNextCard: (state, action: PayloadAction<{ card: TrainingCard, shownTime: string, isCardDirect: boolean }>) => {
            const { card, shownTime, isCardDirect } = action.payload;

            state.optionCards = [card, ...card.options!];
            state.shownTime = shownTime;
            state.isCardDirect = isCardDirect;
            state.isExampleShown = false;

            updateCardParts(state, card, isCardDirect);
            updateUserChoice(state, card, false)
        },

        chooseOption: (state, action: PayloadAction<{ option: CardOption | undefined, card: TrainingCard }>) => {
            state.chosenOption = action.payload.option;
            updateUserChoice(state, action.payload.card, true)
        },

        resetTimer: (state, action: PayloadAction<string>) => {
            state.shownTime = action.payload;
        },

        showExample: state => {
            state.isExampleShown = true;
        },
    },

    extraReducers: (builder) => {
        builder.addCase('training/reset', (state, action) => {
            return initialState;
        });

        builder.addCase('training/refreshCardData', (state, action) => {
            const data = (action as PayloadAction<TrainingCardData>).payload;
            if (data.card?.trainingType === TrainingType.Choice) {
                updateCardParts(state, data.card!, state.isCardDirect);
                updateUserChoice(state, data.card!, data.isSolutionShown)
            }
        });
    }
})

function updateCardParts(state: ChoiceState, card: TrainingCard, isCardDirect: boolean) {
    let k = card.modeInfo.modeSetting.taskKind;

    const isPhraseFirst = !card.pronunciation || k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation || k === TaskKind.PhraseForms || k === TaskKind.Phrase;
    const isPronunciationFirst = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase || k === TaskKind.PronunciationForms || k === TaskKind.Pronunciation)

    const isPhraseSecond = card.pronunciation && (k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationPhrase);
    const isPronunciationSecond = card.pronunciation && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhrasePronunciation);

    const isFormsThird = card.forms && (k === TaskKind.PhrasePronunciationForms || k === TaskKind.PhraseForms || k === TaskKind.PronunciationPhraseForms || k === TaskKind.PronunciationForms)

    state.options = state.optionCards
        .map(vc => {
            const first = isPronunciationFirst ? vc.pronunciation : vc.phrase;
            const second = isPhraseSecond ? vc.phrase : isPronunciationSecond ? vc.pronunciation : '';
            const third = isFormsThird ? vc.forms : '';

            const content = isCardDirect ? vc.meaning : first;
            const contentAux = isCardDirect ? '' : second;
            const contentForms = isCardDirect ? '' : third;

            const wrongOption = isCardDirect ? vc.phrase : vc.meaning;
            const width = getTextWidth(content, 20) + getTextWidth(contentAux, 15);
            return {
                content,
                contentAux,
                contentForms,
                wrongOption,
                card: vc,
                width
            };
        })
        .sort((a, b) => a.width - b.width);

    const first = isPronunciationFirst ? card.pronunciation : card.phrase;
    const second = isPhraseSecond ? card.phrase : isPronunciationSecond ? card.pronunciation : '';
    const third = isFormsThird ? card.forms : '';

    state.task = isCardDirect ? first : card.meaning;
    state.taskAux = isCardDirect ? second : '';
    state.taskForms = isCardDirect ? third : '';

    state.solution = isCardDirect ? card.meaning : first;
    state.solutionAux = isCardDirect ? '' : second;
    state.solutionForms = isCardDirect ? '' : third;

    state.example = card.example;
}

function updateUserChoice(state: ChoiceState, card: TrainingCard, isSolutionShown: boolean) {
    if (isSolutionShown) {
        const backgrounds = ['', '', '', ''];

        const chosenIndex = state.chosenOption ? state.options.findIndex(x => x.card.id === state.chosenOption?.card.id) : -1;
        const isCorrect = state.solution === state.chosenOption?.content;

        if (isCorrect)
            backgrounds[chosenIndex] = 'ch-green';
        else {
            const correctIndex = state.options.findIndex(x => x.card.id === card.id);
            backgrounds[correctIndex] = 'ch-green';
            backgrounds[chosenIndex === -1 ? backgrounds.length : chosenIndex] = 'ch-red';
        }

        state.backgrounds = backgrounds;
        state.wrongOption = !isCorrect && chosenIndex !== -1 ? state.options[chosenIndex] : undefined;
    }
    else {
        state.backgrounds = ['', '', '', ''];
        state.wrongOption = undefined;
    }
}

function getTextWidth(text: string, size: number) {
    const canvas = (getTextWidth as any).canvas || ((getTextWidth as any).canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    context.font = `bold ${size}pt arial`;
    const metrics = context.measureText(text);
    return metrics.width as number;
}

const setNextCard = (card: TrainingCard) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const phraseLengthSum = card.options!.reduce((sum, el) => sum += el.phrase.length, 0);
        const meaningLengthSum = card.options!.reduce((sum, el) => sum += el.meaning.length, 0);
        const maxLengthRatio = 2;
        const isCardDirect = true;
        //const isCardDirect = phraseLengthSum > meaningLengthSum * maxLengthRatio ? true : meaningLengthSum > phraseLengthSum * maxLengthRatio ? false : Math.random() > 0.5;
        dispatch(slice.actions.setNextCard({ card: card, shownTime: new Date().toISOString(), isCardDirect }));
    }
}

const chooseOption = (option: CardOption | undefined) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        const training = getState().training;
        const state = getState().choice;

        const isMistake = option?.content !== state.solution;
        dispatch(trainingActionCreators.showSolution(isMistake));
        dispatch(slice.actions.chooseOption({ option, card: training.card! }));
    }
};

const resetTimer = () => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(slice.actions.resetTimer(new Date().toISOString()));
    }
}

export const choiceReducer = slice.reducer;

export const choiceActionCreators = {
    setNextCard,
    chooseOption,
    resetTimer,
    showExample: slice.actions.showExample,
}
